/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { GridRowModel, GridRenderCellParams } from '@mui/x-data-grid'
import { BoSegments } from '@components/systems/backoffice/BoSegments'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'
import { CardModel, renderCellShim, formattedValue, renderStatus } from './gridCard'

interface PropsType {
	model: CardModel
	row: GridRowModel
	apiRef: any
	renderCell?: { [key: string]: (params: GridRenderCellParams) => JSX.Element }
	/** 폰 = 섹션 탭 · 그 외 = 전부 세로로 */
	tabbed: boolean
}

/**
 * 행 하나의 자세히 — 카드 힌트의 섹션을 스펙 행으로 (RESPONSIVE_PLAN §5.3·§6).
 * 페이지가 `onDetailClick` 을 주면 이 화면은 안 쓰이고 페이지의 자세히(오더·배송)가 뜬다.
 */
export const GridRowDetail = (props: PropsType) => {
	const { model, row, apiRef } = props
	const [tab, setTab] = React.useState<string>(model.sections[0]?.title ?? '')

	React.useEffect(() => {
		setTab(model.sections[0]?.title ?? '')
	}, [model, row.id])

	const titleCol = model.byField[model.title]
	const titleText = titleCol ? formattedValue(titleCol, row, apiRef) : String(row.id)
	const statusCol = model.status ? model.byField[model.status] : null

	const sections =
		props.tabbed && model.sections.length > 1
			? model.sections.filter(s => s.title === tab)
			: model.sections

	return (
		<View css={style} className="boRowDetail">
			<View className="boRowDetailHead">
				<span className="boRowDetailTitle">{titleText}</span>
				{statusCol ? (
					<span className="boRowDetailStatus">
						{renderStatus(statusCol, row, apiRef, props.renderCell?.[statusCol.field])}
					</span>
				) : null}
			</View>

			{props.tabbed && model.sections.length > 1 ? (
				<BoSegments
					variant="tabs"
					items={model.sections.map(s => ({ id: s.title, label: s.title }))}
					value={tab}
					onChange={setTab}
				/>
			) : null}

			{sections.map(section => (
				<View className="boRowDetailSection" key={section.title}>
					{!props.tabbed || model.sections.length <= 1 ? (
						<span className="boRowDetailSectionTitle">{section.title}</span>
					) : null}
					{section.fields.map(field => {
						const col = model.byField[field]
						if (!col) return null
						return (
							<View className="boRowDetailRow" key={field}>
								<span className="boRowDetailLabel">
									{String(col.headerName ?? field)}
								</span>
								<span className="boRowDetailValue">
									{renderCellShim(col, row, apiRef, props.renderCell?.[field])}
								</span>
							</View>
						)
					})}
				</View>
			))}
		</View>
	)
}

const style = css({
	fontFamily: boFont.ui,
	color: mono.ink,

	'.boRowDetailHead': {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		padding: '20px 0 16px',
		'.boRowDetailTitle': {
			flex: 1,
			minWidth: 0,
			fontSize: 22,
			fontWeight: 300,
			lineHeight: 1.2,
			letterSpacing: '-0.01em',
			wordBreak: 'break-all',
		},
		'.boRowDetailStatus': { display: 'flex', gap: 4, flexShrink: 0 },
	},

	'.boRowDetailSection': {
		paddingTop: 12,
		'.boRowDetailSectionTitle': {
			display: 'block',
			padding: '12px 0 4px',
			fontSize: 11,
			letterSpacing: '.2em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontWeight: 500,
		},
	},

	'.boRowDetailRow': {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 120px) minmax(0, 1fr)',
		gap: 12,
		alignItems: 'center',
		minHeight: 44,
		padding: '8px 0',
		borderBottom: `1px solid ${mono.line}`,
		'.boRowDetailLabel': {
			fontSize: 11,
			letterSpacing: '.12em',
			textTransform: 'uppercase',
			color: mono.faint,
			fontWeight: 500,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},
		'.boRowDetailValue': {
			fontSize: 15,
			fontWeight: 300,
			color: mono.ink,
			fontVariantNumeric: 'tabular-nums',
			wordBreak: 'break-word',
			display: 'flex',
			alignItems: 'center',
			gap: 4,
			flexWrap: 'wrap',
			'& a': { color: mono.ink },
		},
	},
})
